// ─── Автопроверки распознанного материала ────────────────────────────────────
//
// Чистые функции без зависимостей: на вход — массив уроков (ровно тот JSON,
// который уходит на сервер), на выход — плоский список проблем с адресом
// (индекс урока + id блока), по которому панель предпросмотра умеет прыгать.
//
// Набор проверок выведен из реальных граблей PDF-парсера, см.
// Servise_gn/pdf-importer/rules/{SS,YSS,MR}.md (разделы «Особенности парсинга»
// и «Changelog»): мусорные глифы от декоративных элементов вёрстки,
// несклеенные переносы по дефису, разорванные и, наоборот, склеенные абзацы,
// нераспознанные библейские ссылки из-за неразрывных пробелов.

export const SEVERITY_ORDER = ['error', 'warn', 'info'];

export const SEVERITY_META = {
  error: { label: 'Ошибки', short: 'Ошибка', color: '#dc2626', bg: '#fef2f2' },
  warn: { label: 'Предупреждения', short: 'Предупреждение', color: '#b45309', bg: '#fffbeb' },
  info: { label: 'Замечания', short: 'Замечание', color: '#0369a1', bg: '#f0f9ff' },
};

// Каталог проверок: код → как показывать в фильтре панели.
export const CHECK_META = {
  'unknown-type': { severity: 'error', title: 'Неизвестный тип блока' },
  'empty-content': { severity: 'error', title: 'Урок без содержимого' },
  'garbage-chars': { severity: 'error', title: 'Мусорные символы' },
  'bad-date': { severity: 'error', title: 'Некорректная дата' },
  'date-order': { severity: 'error', title: 'Даты не по возрастанию' },
  'date-duplicate': { severity: 'error', title: 'Повтор даты' },
  'link-not-in-text': { severity: 'error', title: 'Ссылка не найдена в тексте' },
  'link-no-data': { severity: 'error', title: 'Ссылка без разбора' },
  'link-unknown-book': { severity: 'error', title: 'Неизвестная книга Библии' },

  'hyphen-break': { severity: 'warn', title: 'Незаконченный перенос' },
  'paragraph-split': { severity: 'warn', title: 'Разорванный абзац' },
  'text-truncated': { severity: 'warn', title: 'Текст обрывается' },
  'text-continuation': { severity: 'warn', title: 'Начало со строчной буквы' },
  'unbalanced-pairs': { severity: 'warn', title: 'Незакрытые кавычки или скобки' },
  'glued-words': { severity: 'warn', title: 'Слипшиеся слова' },
  'list-glued': { severity: 'warn', title: 'Склеенные пункты списка' },
  'block-too-long': { severity: 'warn', title: 'Аномально длинный блок' },
  'block-too-short': { severity: 'warn', title: 'Аномально короткий блок' },
  'block-duplicate': { severity: 'warn', title: 'Повтор блока' },
  'link-missed': { severity: 'warn', title: 'Похоже на нераспознанную ссылку' },
  'link-empty-verses': { severity: 'warn', title: 'Стихи не разобраны' },
  'date-gap': { severity: 'warn', title: 'Пропуск в датах' },
  'date-step': { severity: 'warn', title: 'Необычный шаг дат' },
  'count-mismatch': { severity: 'warn', title: 'Не сходится количество' },
  'no-main-text': { severity: 'warn', title: 'День без основного текста' },
  'lesson-missing-block': { severity: 'warn', title: 'Не хватает обязательного блока' },

  'whitespace': { severity: 'info', title: 'Лишние пробелы' },
  'link-cross-chapter': { severity: 'info', title: 'Межглавный диапазон' },
  'empty-name': { severity: 'info', title: 'Пустой заголовок' },
};

// Ожидания по разделам: шаг между записями и контрольные количества.
// SS — 13 уроков по 6 дней (Вс–Пт), между уроками разрыв в 2 дня (суббота
// отдельной записи не имеет). YSS — 13 недель ровно по 7 дней. MR/ER — год.
const SECTION_RULES = {
  ss: { steps: [1, 2], perLesson: 6, requireInLesson: ['bibleVersesTitle', 'memoryVerse'] },
  yss: { steps: [7], total: [13] },
  mr: { steps: [1], total: [365, 366] },
  er: { steps: [1], total: [365, 366] },
};

// ─── Регулярки ───────────────────────────────────────────────────────────────

const TRAILING_HYPHEN_RE = /[а-яё]-$/i;
const TERMINAL_RE = /[.!?…:;»"'”)\]]$/;
const GLUED_WORDS_RE = /[а-яё][А-ЯЁ]/;
const DOT_UPPER_RE = /[а-яё]\.[А-ЯЁ]/;
const STARTS_LOWER_RE = /^[а-яё]/;
const NUMBERED_ITEM_RE = /(^|\s)(\d{1,2})[.)]\s+[А-ЯЁ«„"]/g;
// Слова, с которых начинается ложный кандидат (нумерация уроков, время и пр.).
const CANDIDATE_STOPWORDS = /^(?:\d\s?)?(?:Урок|Неделя|Глава|Стр|Рис|Табл|Тел)\b/i;

// ─── Поиск нераспознанных кандидатов на ссылку ─────────────────────────────
// Тот же принцип, что и в pdf-importer/audit_missed_links.py: шаблон ЗАВЕДОМО
// шире основного (там — регэксп findsBibleLink), поэтому это не источник
// истины, а подсказка «сюда стоит посмотреть». Апокрифы («1 Еноха 1:9») и
// склонённые формы книг без падежных алиасов сюда тоже попадут — это
// ожидаемо, финальное решение всегда за человеком.
//
// Живёт здесь, а не в UploadTexts.jsx, чтобы подсветка кандидата прямо в
// тексте предпросмотра и строка о нём в панели проверок считались одной и
// той же функцией: расхождение двух копий одной эвристики этот проект уже
// проходил (см. changelog в pdf-importer/rules/SS.md).
const CANDIDATE_REF_RE = /(\d\s?)?[А-ЯЁ][а-яё]{1,8}\.?\s*\d{1,3}[:.]\d{1,3}/g;

export function findCandidateGaps(text, links) {
  if (!text) return [];
  const linkedTexts = (links || []).map(l => l.text);
  const gaps = [];
  let m;
  CANDIDATE_REF_RE.lastIndex = 0;
  while ((m = CANDIDATE_REF_RE.exec(text))) {
    const candidate = m[0];
    const covered = linkedTexts.some(lt => candidate.includes(lt) || lt.includes(candidate));
    if (!covered) gaps.push(candidate);
  }
  return gaps;
}

const MAX_BLOCK_LEN = 2000;
const MIN_BLOCK_LEN = 3;
const MAX_DATE_GAP = 8;

// ─── Хелперы ─────────────────────────────────────────────────────────────────

// Свой парсер вместо new Date(): формат «2026-09-27 00:00:00.000» Safari
// разбирать не обязан, а падать на нём проверка дат не должна.
export function parseLessonDate(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const ts = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  const date = new Date(ts);
  if (date.getUTCFullYear() !== Number(y) || date.getUTCMonth() !== Number(mo) - 1 || date.getUTCDate() !== Number(d)) {
    return null; // 31 февраля и подобное
  }
  return date;
}

function daysBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function excerptAround(text, index, radius = 45) {
  const from = Math.max(0, index - radius);
  const to = Math.min(text.length, index + radius);
  return (from > 0 ? '…' : '') + text.slice(from, to).replace(/\s+/g, ' ') + (to < text.length ? '…' : '');
}

function shorten(text, len = 90) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > len ? clean.slice(0, len) + '…' : clean;
}

function normalizeForCompare(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function countOf(text, ch) {
  let n = 0;
  for (let i = 0; i < text.length; i++) if (text[i] === ch) n++;
  return n;
}

// Мусорный символ в тексте: управляющие коды (тот самый \x08 из декоративной
// «линии для ответа»), U+FFFD «ромбик с вопросом» и приватная область Unicode
// (глифы символьных шрифтов вроде Wingdings-буллитов YSS). Проверка по кодам,
// а не регуляркой — такие литералы в исходнике нечитаемы и легко ломаются.
function findGarbageChar(text) {
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c < 32 && c !== 9 && c !== 10 && c !== 13) return i;
    if (c === 127 || c === 0xFFFD) return i;
    if (c >= 0xE000 && c <= 0xF8FF) return i;
  }
  return -1;
}

// Номер сноски в конце абзаца — обычной цифрой («…спасти каждого»21) или
// надстрочной («…принес!»29). MR печатает их сплошь, и без отсечения любой
// такой абзац выглядит оборванным на полуслове.
function stripFootnoteMarker(text) {
  let end = text.length;
  while (end > 0) {
    const c = text.charCodeAt(end - 1);
    const isDigit = c >= 48 && c <= 57;
    const isSuper = c === 0x00B2 || c === 0x00B3 || c === 0x00B9 || (c >= 0x2070 && c <= 0x2079);
    if (!isDigit && !isSuper) break;
    end--;
  }
  return end === text.length ? text : text.slice(0, end);
}

// Неразрывный/тонкий пробел (их должна была схлопнуть normalize_ws в парсере)
// или двойной обычный пробел.
function findWhitespaceIssue(text) {
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 0x00A0 || (c >= 0x2000 && c <= 0x200A) || c === 0x202F || c === 0x205F || c === 0x3000) return i;
    if (c === 32 && text.charCodeAt(i + 1) === 32) return i;
  }
  return -1;
}

// ─── Проверки одного блока ───────────────────────────────────────────────────

function checkBlockText(block, ctx, push, neighbours = {}) {
  const text = typeof block.text === 'string' ? block.text : '';
  const kind = ctx.kindOf(block.type);
  const isProse = kind === 'main' || kind === 'verse' || kind === 'question' || kind === 'list';

  if (!text.trim()) {
    push('block-too-short', 'Блок без текста', 'Пустой блок дойдёт до приложения как пустая строка.');
    return;
  }

  const at = findGarbageChar(text);
  if (at >= 0) {
    const code = text.codePointAt(at).toString(16).toUpperCase().padStart(4, '0');
    push(
      'garbage-chars',
      `Мусорный символ U+${code} в тексте`,
      'Обычно это остаток декоративного элемента вёрстки (линия для ответа, буллит символьного шрифта), приклеившийся к тексту.',
      excerptAround(text, at),
    );
  }

  const wsAt = findWhitespaceIssue(text);
  if (wsAt >= 0 || text !== text.trim()) {
    push(
      'whitespace',
      'Лишние или неразрывные пробелы',
      'Такой пробел ломает распознавание ссылок вида «1 Царств» и виден в приложении как разрыв.',
      excerptAround(text, Math.max(0, wsAt)),
    );
  }

  // Обрыв абзаца. Самый надёжный признак — пара соседних блоков: этот кончается
  // без точки, а следующий начинается со строчной буквы. Ровно так выглядит
  // «плавающий» перенос YSS (rules/YSS.md, пп. 1 и 4), не сросшийся при разборе.
  const trimmed = text.trim();
  const withoutNote = stripFootnoteMarker(trimmed).trim();
  // Абзац, заканчивающийся самой библейской ссылкой («Способы общения: • Рим.
  // 1:19, 20»), знака препинания в конце не требует.
  const endsWithLink = (Array.isArray(block.links) ? block.links : [])
    .some((l) => l && typeof l.text === 'string' && l.text && trimmed.endsWith(l.text.trim()));
  const endsOpen = withoutNote.length > 0 && !TERMINAL_RE.test(withoutNote) && !endsWithLink;
  const nextText = typeof neighbours.next?.text === 'string' ? neighbours.next.text.trim() : '';
  const prevText = typeof neighbours.prev?.text === 'string' ? neighbours.prev.text.trim() : '';
  const nextStartsLower = STARTS_LOWER_RE.test(nextText);
  const prevEndsOpen = prevText.length > 0 && !TERMINAL_RE.test(stripFootnoteMarker(prevText).trim());

  if (TRAILING_HYPHEN_RE.test(trimmed)) {
    push(
      'hyphen-break',
      'Текст обрывается переносом по дефису',
      'Перенос не склеился со следующим блоком — вторая половина слова, скорее всего, потерялась или ушла в соседний блок.',
      shorten(text.slice(-70)),
    );
  } else if (isProse && endsOpen && nextStartsLower) {
    push(
      'paragraph-split',
      'Абзац разорван: продолжение попало в следующий блок',
      'Один абзац оригинала приехал двумя блоками — в приложении он разъедется на два куска.',
      `…${shorten(text.slice(-60), 60)} ⟶ ${shorten(nextText.slice(0, 60), 60)}…`,
    );
  } else if (isProse && kind !== 'list' && trimmed.length > 40 && endsOpen) {
    push(
      'text-truncated',
      'Блок кончается без знака препинания',
      'Похоже на оборванный абзац: конец предложения не дошёл до JSON.',
      shorten(text.slice(-70)),
    );
  }

  // Начало со строчной буквы само по себе — сигнал только когда предыдущий блок
  // закончился нормально (иначе это уже учтено как paragraph-split выше).
  if (isProse && STARTS_LOWER_RE.test(trimmed) && !prevEndsOpen) {
    push(
      'text-continuation',
      'Блок начинается со строчной буквы',
      'Вероятно, это продолжение абзаца, потерявшее своё начало.',
      shorten(text.slice(0, 70)),
    );
  }

  if (isProse && kind !== 'list') {
    const glued = text.search(GLUED_WORDS_RE);
    if (glued >= 0) {
      push(
        'glued-words',
        'Слипшиеся слова (строчная буква вплотную к заглавной)',
        'Обычно это приклеившаяся буквица (MR) или потерянный пробел на стыке фрагментов.',
        excerptAround(text, glued),
      );
    } else {
      const dotUpper = text.search(DOT_UPPER_RE);
      if (dotUpper >= 0) {
        push(
          'glued-words',
          'Нет пробела после точки',
          'Предложения склеились — вероятно, потерян пробел на стыке PDF-фрагментов.',
          excerptAround(text, dotUpper),
        );
      }
    }

    // Несколько нумерованных пунктов в одном блоке: считаем проблемой только
    // подряд идущие возрастающие номера (иначе ловится обычная «…, 2. Затем»
    // на конце предложения со стихом).
    NUMBERED_ITEM_RE.lastIndex = 0;
    const nums = [];
    let m;
    while ((m = NUMBERED_ITEM_RE.exec(text)) !== null) nums.push({ n: Number(m[2]), at: m.index });
    for (let i = 1; i < nums.length; i++) {
      if (nums[i].n === nums[i - 1].n + 1 && nums[i].at > 0) {
        push(
          'list-glued',
          `Пункты ${nums[i - 1].n} и ${nums[i].n} нумерованного списка в одном блоке`,
          'Разбивка списка не сработала — в приложении пункты слипнутся в один абзац.',
          excerptAround(text, nums[i].at),
        );
        break;
      }
    }
  }

  if (text.length > ctx.longBlockLimit) {
    push(
      'block-too-long',
      `Блок на ${text.length} символов при обычных ${ctx.medianBlockLen} в этом файле`,
      'Похоже, несколько абзацев не разделились и приехали одним куском.',
      shorten(text, 120),
    );
  } else if (isProse && text.trim().length < MIN_BLOCK_LEN) {
    push('block-too-short', `Блок из ${text.trim().length} символов`, 'Скорее всего, обрывок вёрстки, а не содержимое.', text);
  }
}

// ─── Проверки ссылок в блоке ─────────────────────────────────────────────────

function checkBlockLinks(block, ctx, push) {
  const text = typeof block.text === 'string' ? block.text : '';
  const links = Array.isArray(block.links) ? block.links : [];

  links.forEach((link) => {
    const linkText = link && typeof link.text === 'string' ? link.text : '';
    if (!linkText) {
      push('link-no-data', 'Ссылка без текста', 'Пустая ссылка не подсветится и не откроется в приложении.');
      return;
    }
    if (!text.includes(linkText)) {
      push(
        'link-not-in-text',
        `Ссылка «${linkText}» не найдена в тексте блока`,
        'Подсветка в приложении не сработает: текст ссылки разошёлся с текстом блока.',
        shorten(text),
        linkText,
      );
    }
    const data = Array.isArray(link.data) ? link.data : [];
    if (data.length === 0) {
      push('link-no-data', `Ссылка «${linkText}» не разобрана`, 'Книга, глава и стихи не определены — переход в Библию не сработает.', undefined, linkText);
      return;
    }
    data.forEach((d) => {
      if (!ctx.knownBooks.has(Number(d.bookNumber))) {
        push('link-unknown-book', `Ссылка «${linkText}»: неизвестный код книги ${d.bookNumber ?? '—'}`, 'Такого номера нет в справочнике книг — приложение не откроет отрывок.', undefined, linkText);
      }
      const hasVerseNumbers = /\d\s*[:.]\s*\d/.test(linkText);
      if (hasVerseNumbers && (!Array.isArray(d.verses) || d.verses.length === 0)) {
        push('link-empty-verses', `Ссылка «${linkText}»: стихи не разобраны`, 'В тексте стихи указаны, а в данных пусто — откроется вся глава.', undefined, linkText);
      }
    });
    if (/\d+\s*:\s*\d+\s*[–—-]\s*\d+\s*:\s*\d+/.test(linkText)) {
      push('link-cross-chapter', `Межглавный диапазон «${linkText}»`, 'В данных хранится только начальный стих — проверьте вручную, так ли это задумано.', undefined, linkText);
    }
  });

  // Нераспознанные кандидаты — та же findCandidateGaps, что подсвечивает их
  // прямо в тексте предпросмотра: одна реализация на подсветку и на список.
  const seen = new Set();
  findCandidateGaps(text, links).forEach((candidate) => {
    const key = normalizeForCompare(candidate);
    if (!key || seen.has(key)) return;
    seen.add(key);
    // В списке шум дороже, чем в подсветке: одиночный ложный кандидат в
    // тексте глаз не мозолит, а в панели он занимает строку наравне с
    // настоящей находкой. Поэтому здесь дополнительно отсекаем нумерацию.
    if (CANDIDATE_STOPWORDS.test(key)) return;
    push(
      'link-missed',
      `«${key}» похоже на библейскую ссылку, но не распознано`,
      'Проверьте сокращение книги: чаще всего виноват неразрывный пробел или форма названия, которой нет в таблице Service.ts.',
      excerptAround(text, Math.max(0, text.indexOf(candidate))),
      candidate,
    );
  });
}

// ─── Публичный API ───────────────────────────────────────────────────────────

/**
 * @param {Array} lessons  разобранный JSON уроков
 * @param {Object} opts
 * @param {string} opts.section   ключ раздела: ss | yss | mr | er
 * @param {Array}  opts.knownTypes  типы блоков, которые умеет рисовать превью
 * @param {Array}  opts.knownBooks  известные коды книг Библии
 * @param {Function} opts.kindOf    тип блока → вид (main/title/question/...)
 * @returns {Array} список проблем в порядке следования по документу
 */
export function runChecks(lessons, opts = {}) {
  const { section = 'ss', knownTypes = [], knownBooks = [], kindOf = () => 'unknown' } = opts;
  if (!Array.isArray(lessons)) return [];

  // Порог «аномально длинного» блока считаем от самого файла: у Субботней школы
  // абзац на 2900 символов — норма, у МСШ такой же — уже слипшиеся абзацы.
  const proseLengths = [];
  lessons.forEach((l) => (l && Array.isArray(l.content) ? l.content : []).forEach((b) => {
    if (b && typeof b.text === 'string' && kindOf(b.type) === 'main') proseLengths.push(b.text.length);
  }));
  proseLengths.sort((a, b) => a - b);
  const medianBlockLen = proseLengths.length ? proseLengths[Math.floor(proseLengths.length / 2)] : 0;

  const ctx = {
    knownTypes: new Set(knownTypes),
    knownBooks: new Set(knownBooks.map(Number)),
    kindOf,
    medianBlockLen,
    longBlockLimit: Math.max(MAX_BLOCK_LEN, medianBlockLen * 5),
  };
  const rules = SECTION_RULES[section] || {};
  const issues = [];
  let seq = 0;

  const makePush = (lessonIndex, blockIndex, blockId) => (code, message, hint, excerpt, fragment) => {
    issues.push({
      key: `i${seq++}`,
      code,
      severity: (CHECK_META[code] || {}).severity || 'warn',
      title: (CHECK_META[code] || {}).title || code,
      lessonIndex,
      blockIndex,
      blockId,
      message,
      hint,
      excerpt,
      // Точная подстрока внутри текста блока, к которой относится замечание.
      // Панель по ней подсвечивает и прокручивает не весь блок, а сам фрагмент.
      fragment,
    });
  };

  let prevDate = null;
  const seenDates = new Map();
  const perLesson = new Map();

  lessons.forEach((lesson, lessonIndex) => {
    const lessonPush = makePush(lessonIndex, null, null);

    // ── даты ──
    const date = parseLessonDate(lesson && lesson.date);
    if (!date) {
      lessonPush('bad-date', `Дата «${lesson && lesson.date ? lesson.date : '—'}» не распознана`, 'Ожидается формат YYYY-MM-DD — приложение не сможет показать этот день.');
    } else {
      const iso = date.toISOString().slice(0, 10);
      if (seenDates.has(iso)) {
        lessonPush('date-duplicate', `Дата ${iso} уже была у записи №${seenDates.get(iso) + 1}`, 'Две записи на один день — одна перекроет другую.');
      } else {
        seenDates.set(iso, lessonIndex);
      }
      if (prevDate) {
        const step = daysBetween(prevDate, date);
        if (step <= 0) {
          lessonPush(
            'date-order',
            `Дата ${iso} не позже предыдущей (${prevDate.toISOString().slice(0, 10)})`,
            'Классический признак квартала, переходящего через Новый год: декабрьским датам не сдвинули год на предыдущий.',
          );
        } else if (step > MAX_DATE_GAP) {
          lessonPush('date-gap', `Разрыв ${step} дн. после предыдущей записи`, 'В материале пропущены дни — проверьте, не потерялся ли урок при разборе.');
        } else if (Array.isArray(rules.steps) && !rules.steps.includes(step)) {
          lessonPush('date-step', `Шаг ${step} дн. вместо ожидаемого ${rules.steps.join(' или ')}`, 'Для этого раздела интервал между записями обычно другой.');
        }
      }
      prevDate = date;
    }

    if (!lesson || !Array.isArray(lesson.content) || lesson.content.length === 0) {
      lessonPush('empty-content', 'У записи нет ни одного блока содержимого', 'Такой день откроется в приложении пустым.');
      return;
    }

    if (!lesson.name || !String(lesson.name).trim()) {
      lessonPush('empty-name', 'Пустой заголовок записи', 'В списке уроков и в шапке дня будет «Без названия».');
    }

    if (lesson.lessonNumber != null) {
      const arr = perLesson.get(lesson.lessonNumber) || [];
      arr.push(lessonIndex);
      perLesson.set(lesson.lessonNumber, arr);
    }

    // ── блоки ──
    let mainCount = 0;
    const seenTexts = new Map();

    lesson.content.forEach((block, blockIndex) => {
      const blockId = block && block.id != null ? block.id : blockIndex;
      const push = makePush(lessonIndex, blockIndex, blockId);
      const type = block && block.type;

      if (typeof type === 'string' && type.startsWith('UNKNOWN:')) {
        push('unknown-type', `Парсер не опознал стиль: ${type.slice(8)}`, 'Шрифт и кегль этого фрагмента отсутствуют в таблице classify() — блок попал в JSON как есть.', shorten(block.text));
      } else if (!ctx.knownTypes.has(type)) {
        push('unknown-type', `Тип «${type ?? '—'}» неизвестен предпросмотру`, 'Приложение такой блок не отрисует — либо это опечатка в типе, либо нужен новый стиль.', shorten(block && block.text));
      }

      const kind = ctx.kindOf(type);
      if (kind === 'main') mainCount++;

      checkBlockText(block || {}, ctx, push, {
        prev: lesson.content[blockIndex - 1],
        next: lesson.content[blockIndex + 1],
      });
      checkBlockLinks(block || {}, ctx, push);

      // Дубликаты ищем только среди содержательных блоков одного типа: повтор
      // заголовка («Вопросы для размышления:» в каждом дне YSS) — норма, а
      // памятный стих, процитированный ещё раз в тексте дня, — не дубль.
      const norm = normalizeForCompare(block && block.text);
      const dupKey = `${type} ${norm}`;
      if (norm.length > 15 && (kind === 'main' || kind === 'question')) {
        if (seenTexts.has(dupKey)) {
          push('block-duplicate', `Текст повторяет блок №${seenTexts.get(dupKey) + 1}`, 'Чаще всего это просочившийся колонтитул или дважды собранный абзац.', shorten(norm));
        } else {
          seenTexts.set(dupKey, blockIndex);
        }
      }
    });

    // Кавычки и скобки считаем по всей записи, а не по блоку: многоабзацная
    // цитата по правилам русской типографики открывает « в каждом абзаце, а
    // закрывает » только в последнем — поблочная проверка на этом захлёбывается.
    const lessonText = lesson.content.map((b) => (b && typeof b.text === 'string' ? b.text : '')).join('\n');
    const pairs = [
      // У внутренних кавычек закрывающей бывает и „…“, и „…”.
      { open: '«', closers: ['»'], label: 'кавычки «»', bothWays: true },
      { open: '„', closers: ['“', '”'], label: 'внутренние кавычки', bothWays: true },
      // У скобок сигнал только в одну сторону: лишние закрывающие — это
      // нумерация вида «1)», а вот незакрытая «(» означает потерянный текст.
      { open: '(', closers: [')'], label: 'скобки', bothWays: false },
    ];
    for (const { open, closers, label, bothWays } of pairs) {
      const a = countOf(lessonText, open);
      const b = closers.reduce((sum, ch) => sum + countOf(lessonText, ch), 0);
      if (a > b || (bothWays && a !== b)) {
        lessonPush(
          'unbalanced-pairs',
          `По всей записи не сходятся ${label}: ${a} открывающих и ${b} закрывающих`,
          'Часть текста, скорее всего, потерялась при разборе или абзац разрезан не там.',
        );
        break;
      }
    }

    if (mainCount === 0) {
      lessonPush('no-main-text', 'В записи нет ни одного блока основного текста', 'Есть заголовки и вопросы, но сам текст дня не собрался.');
    }
  });

  // ── контрольные количества ──
  if (Array.isArray(rules.total) && lessons.length && !rules.total.includes(lessons.length)) {
    issues.push({
      key: `i${seq++}`,
      code: 'count-mismatch',
      severity: CHECK_META['count-mismatch'].severity,
      title: CHECK_META['count-mismatch'].title,
      lessonIndex: 0,
      blockIndex: null,
      blockId: null,
      message: `Записей ${lessons.length}, а ожидается ${rules.total.join(' или ')}`,
      hint: 'Для этого раздела количество записей фиксировано — часть материала, вероятно, не разобралась.',
    });
  }

  if (rules.perLesson) {
    perLesson.forEach((indexes, lessonNumber) => {
      if (indexes.length !== rules.perLesson) {
        issues.push({
          key: `i${seq++}`,
          code: 'count-mismatch',
          severity: CHECK_META['count-mismatch'].severity,
          title: CHECK_META['count-mismatch'].title,
          lessonIndex: indexes[0],
          blockIndex: null,
          blockId: null,
          message: `В уроке ${lessonNumber} дней ${indexes.length}, а ожидается ${rules.perLesson}`,
          hint: 'Дни урока собрались не полностью — проверьте границы страниц этого урока.',
        });
      }
    });

    // Номера уроков должны идти подряд с 1 без дыр.
    const numbers = [...perLesson.keys()].sort((a, b) => a - b);
    numbers.forEach((n, i) => {
      const expected = i + 1;
      if (n !== expected) {
        issues.push({
          key: `i${seq++}`,
          code: 'count-mismatch',
          severity: CHECK_META['count-mismatch'].severity,
          title: CHECK_META['count-mismatch'].title,
          lessonIndex: perLesson.get(n)[0],
          blockIndex: null,
          blockId: null,
          message: `После урока ${numbers[i - 1] ?? '—'} идёт урок ${n}`,
          hint: 'В нумерации уроков дыра — какой-то урок не попал в разбор.',
        });
      }
    });
  }

  // ── обязательные блоки внутри урока (SS: стихи для исследования + памятный стих) ──
  if (Array.isArray(rules.requireInLesson) && rules.requireInLesson.length) {
    perLesson.forEach((indexes, lessonNumber) => {
      const types = new Set();
      indexes.forEach((i) => (lessons[i].content || []).forEach((b) => types.add(b.type)));
      rules.requireInLesson.forEach((needed) => {
        if (!types.has(needed)) {
          issues.push({
            key: `i${seq++}`,
            code: 'lesson-missing-block',
            severity: CHECK_META['lesson-missing-block'].severity,
            title: CHECK_META['lesson-missing-block'].title,
            lessonIndex: indexes[0],
            blockIndex: null,
            blockId: null,
            message: `В уроке ${lessonNumber} нет блока «${needed}»`,
            hint: 'Для этого раздела блок обязателен — вероятно, он не распознался на вводной странице урока.',
          });
        }
      });
    });
  }

  return issues.sort((a, b) => {
    if (a.lessonIndex !== b.lessonIndex) return a.lessonIndex - b.lessonIndex;
    const ai = a.blockIndex == null ? -1 : a.blockIndex;
    const bi = b.blockIndex == null ? -1 : b.blockIndex;
    if (ai !== bi) return ai - bi;
    return SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity);
  });
}

export function countBySeverity(issues) {
  const counts = { error: 0, warn: 0, info: 0 };
  (issues || []).forEach((i) => { counts[i.severity] = (counts[i.severity] || 0) + 1; });
  return counts;
}

/** Проблемы по индексу урока — для бейджей в селекторе. */
export function groupByLesson(issues) {
  const map = new Map();
  (issues || []).forEach((i) => {
    const cur = map.get(i.lessonIndex) || { error: 0, warn: 0, info: 0, total: 0 };
    cur[i.severity] += 1;
    cur.total += 1;
    map.set(i.lessonIndex, cur);
  });
  return map;
}

/** Текстовый отчёт — чтобы отдать редактору. */
export function issuesToText(issues, lessons) {
  return (issues || [])
    .map((i) => {
      const lesson = lessons && lessons[i.lessonIndex];
      const where = `Запись ${i.lessonIndex + 1}${lesson && lesson.date ? ` (${String(lesson.date).slice(0, 10)})` : ''}${i.blockId != null ? `, блок #${i.blockId}` : ''}`;
      return `[${SEVERITY_META[i.severity].short}] ${where}: ${i.message}${i.excerpt ? `\n    ${i.excerpt}` : ''}`;
    })
    .join('\n');
}
